/**
 * Definisi "Active User": user yang membuka aplikasi dalam jendela waktu ini.
 *
 * Sumber datanya field `lastOpenedAt` di `/users/{uid}` yang di-stamp aplikasi
 * saat dibuka — bukan `lastSync`, yang ikut terisi oleh background sync walau
 * aplikasi tidak pernah dibuka user.
 */
export const ACTIVE_WINDOW_DAYS = 30

/** Nama field yang ditulis aplikasi (`AppActivityService.kLastOpenedField`). */
export const LAST_OPENED_FIELD = 'lastOpenedAt'

/**
 * Batas bawah jendela aktif, dibulatkan ke tengah malam.
 *
 * Pembulatan membuat nilainya stabil sepanjang hari, sehingga bisa dipakai
 * sebagai bagian dari query key React Query tanpa memicu refetch tiap render.
 */
export function activeSinceDate(windowDays: number = ACTIVE_WINDOW_DAYS): Date {
  const since = new Date()
  since.setHours(0, 0, 0, 0)
  since.setDate(since.getDate() - windowDays)
  return since
}

export type ActivityTone = 'emerald' | 'stone' | 'amber'

export interface UserActivityStatus {
  label: string
  tone: ActivityTone
  /** Penjelasan lengkap, ditampilkan saat kursor menyentuh badge. */
  description: string
}

/**
 * Terjemahkan `lastOpenedAt` jadi status yang bisa dibaca admin.
 *
 * Tiga keadaan, dan yang ketiga paling penting dibedakan: tidak adanya
 * `lastOpenedAt` BUKAN berarti user tidak pernah membuka aplikasi. Field ini
 * baru dikirim sejak aplikasi versi 1.15, jadi user yang masih memakai versi
 * lama akan selalu tampak kosong walau mereka aktif setiap hari. Label
 * "Never opened" pada versi awal menyatakan hal yang tidak diketahui sebagai
 * fakta, dan itu menyesatkan.
 */
export function describeUserActivity(
  lastOpened: Date | null | undefined,
  activeSince: Date,
): UserActivityStatus {
  if (!lastOpened) {
    return {
      label: 'Belum terpantau',
      tone: 'amber',
      description:
        'Aplikasi belum pernah melaporkan kapan user membukanya. Biasanya karena user masih memakai aplikasi versi lama — bukan berarti tidak pernah dibuka.',
    }
  }

  if (lastOpened.getTime() >= activeSince.getTime()) {
    return {
      label: 'Aktif',
      tone: 'emerald',
      description: `Membuka aplikasi dalam ${ACTIVE_WINDOW_DAYS} hari terakhir.`,
    }
  }

  return {
    label: 'Tidak aktif',
    tone: 'stone',
    description: `Terakhir membuka aplikasi lebih dari ${ACTIVE_WINDOW_DAYS} hari lalu.`,
  }
}
